import api from '@/store/api';
import { athena, atlas } from '@/store/api/instances';
import type { CreateMerchantRequest, Merchant } from '@/types';
import type { ApiExportRequest, ApiListingRequest, ApiResponse } from '@bitstopco/bitstop-theme';

type BlobPart = Blob | ArrayBuffer;

/**
 * RTK Query API for Merchants.
 */
export const merchantsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getMerchants: build.query<ApiResponse<Merchant[]>, ApiListingRequest>({
      query: (data) => {
        return athena({
          url: 'v2/Merchants/find',
          method: 'POST',
          data,
        });
      },
      providesTags: ['Merchants'],
    }),
    getMerchant: build.query<ApiResponse<Merchant>, string>({
      query: (id) =>
        athena({
          url: `v2/Merchants/${id}`,
          method: 'GET',
        }),
      providesTags: (_result, _error, id) => [
        { type: 'Merchants', id },
        { type: 'Merchant', id },
      ],
    }),
    createMerchant: build.mutation<ApiResponse<Merchant>, CreateMerchantRequest>({
      query: (data) =>
        atlas({
          url: '/v2/merchants',
          method: 'POST',
          data,
        }),
      invalidatesTags: ['Merchants'],
    }),
    updateMerchant: build.mutation<ApiResponse<Merchant>, Partial<Merchant> & { id: string }>({
      query: ({ id, ...data }) =>
        atlas({
          url: `/v2/merchants/${id}`,
          method: 'PATCH',
          data,
        }),
      invalidatesTags: (_, __, { id }) => [
        'Merchants',
        { type: 'Merchants', id },
        { type: 'Merchant', id },
      ],
    }),
    exportMerchants: build.mutation<BlobPart, ApiExportRequest>({
      query: ({ type, data }) =>
        athena({
          url: `v2/Merchants/${type}`,
          method: 'POST',
          data,
        }),
    }),
  }),
});

/**
 * Custom hooks for Merchants API
 */

/**
 * Export hooks and endpoints
 */
export const {
  useCreateMerchantMutation,
  useUpdateMerchantMutation,
  useGetMerchantsQuery,
  useGetMerchantQuery,
  useExportMerchantsMutation,
} = merchantsApi;

export const {
  endpoints: { createMerchant, updateMerchant, getMerchants, getMerchant },
} = merchantsApi;
